class CoryCarousel extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => this.setup());
  }

  setup() {
    if (this.initialized) return;
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelector('[data-carousel-dots]');
    if (!this.viewport || !this.track) return;
    this.initialized = true;

    this.onPrev = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.onScroll = () => this.update();
    this.onResize = () => this.refresh();
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this.refresh();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.stopAutoplay();
    this.initialized = false;
  }

  get active() {
    return matchMedia('(max-width: 749px)').matches
      ? this.dataset.mobile === 'slider'
      : this.dataset.desktop === 'slider';
  }

  get items() { return [...this.track.children]; }

  refresh() {
    this.toggleAttribute('active', this.active);
    this.buildDots();
    this.update();
    this.startAutoplay();
  }

  step() {
    const item = this.items[0];
    if (!item) return this.viewport.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
    return item.getBoundingClientRect().width + gap;
  }

  move(direction) {
    if (!this.active) return;
    const atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 4;
    const atStart = this.viewport.scrollLeft <= 4;
    if (direction > 0 && atEnd) this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
    else if (direction < 0 && atStart) this.viewport.scrollTo({ left: this.viewport.scrollWidth, behavior: 'smooth' });
    else this.viewport.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dots) return;
    this.dots.replaceChildren();
    if (!this.active || this.dataset.dots !== 'true') return;
    this.items.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `Ir al elemento ${index + 1}`);
      button.addEventListener('click', () => this.viewport.scrollTo({ left: index * this.step(), behavior: 'smooth' }));
      this.dots.append(button);
    });
  }

  update() {
    const index = Math.round(this.viewport.scrollLeft / Math.max(this.step(), 1));
    [...(this.dots?.children || [])].forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));
    const showArrows = this.active && this.dataset.arrows === 'true';
    if (this.prev) this.prev.hidden = !showArrows;
    if (this.next) this.next.hidden = !showArrows;
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.active || this.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const delay = Math.max(Number(this.dataset.speed || 5), 2) * 1000;
    this.timer = setInterval(() => this.move(1), delay);
  }

  stopAutoplay() { if (this.timer) clearInterval(this.timer); this.timer = null; }
}

if (!customElements.get('cory-carousel')) customElements.define('cory-carousel', CoryCarousel);
